import * as React from 'react';
import styled from '@emotion/styled';
import { useStaticQuery, graphql } from 'gatsby';
import { CenteredContent } from '../../components/utility/section';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { GatsbySite } from '../../graphql';
import SEO from '../../components/seo';
import { SpacingWrapper } from '../../components/utility/spacing-wrapper';
import ResponsiveDiv from '../../components/utility/ResponsiveDiv';
import ResponsiveParagraph from '../../components/utility/ResponsiveParagraph';
import Section from '../../components/project/section';
import { SectionTag } from '../../components/project/section-tag';
import Quote from '../../components/project/quote';
import TitlePage from '../../components/project/TitlePage';
import Tech from '../../components/project/tech';
import Client from '../../components/project/client';
import NextMissions from '../../components/project/next-missions';
// import photos
import twoPhones from '../../images/projects/sol/two-phones.png';
import laptopAndPhone from '../../images/projects/sol/laptop-phone.png';
import infographic from '../../images/projects/sol/infographic.png';
import reactLogo from '../../images/tech-logos/react-logo.png';
import nodeLogo from '../../images/tech-logos/node-logo.png';
import timesheet from '../../images/projects/sol/timesheet.png';
import alberio from '../../images/projects/employee-avatars/alberio-bathory-frota.png';
import userExperiencePhone from '../../images/projects/sol/user-experience-phone.png';
import robbie from '../../images/projects/employee-avatars/robbie.png';
import background from '../../images/projects/sol/background.png';
import finalPhoto from '../../images/projects/sol/final-photo.png';
import javascriptLogo from '../../images/tech-logos/js-logo.png';
import backgroundImage from '../../images/projects/enjoyn/background.png';
import masterpieceLogo from '../../images/projects/sol/masterpiece-logo.png';

const SolPage = () => {
  const { site } = useStaticQuery<GatsbySite>(
    graphql`
      query {
        site {
          siteMetadata {
            projects {
              name
              description
              imageName
              link
            }
          }
        }
      }
    `,
  );

  const { siteMetadata } = site;

  const projects = siteMetadata?.projects || [];
  return (
    <>
      <SEO title="SOL" />
      <div id="#top" />
      <TitlePage
        title="SOL: An all-powerful scheduling app for healthcare facilities"
        content="Some AI, a healthy dose of React, a whole lot of scheduling, convenience and efficiency,
        in one app that combines the needs of staff, residents and administration"
        backgroundImage={backgroundImage}
      />

      <SpacingWrapper>
        <Client
          content={`With retirement residences across Canada, including one in Medicine Hat, Alberta, Masterpiece Retirement are passionate about improving the quality of life for seniors. The organization is focused on delivering exceptional hospitality that enriches the lives of their residents at an incredible value. `}
          images={[{ image: masterpieceLogo, url: 'https://www.masterpieceretirement.com/' }]}
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={twoPhones}
              alt="Two phones displaying the SOL app"
              style={{ width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>

      <SpacingWrapper>
        <Section
          p1={`Retirement residences are almost self-sufficient communities that are focused on providing excellent care for senior residents. Masterpiece is no different with staff across nursing, administration, maintenance, kitchen, housekeeping departments and more.\nTo top it all, providing 24x7 services magnifies day-to-day tasks - scheduling, accounting, regulatory requirements - in scale and complexity.`}
          p2={`SOL set out to tackle these optimization issues, which is among the most complex tasks, especially when the size of the organization is huge.\nThe app was focused on automating a lot of the manual processes that kept track of staff schedules, overtime requirements, and employee communications.\nIt did so by using custom algorithms, integration with accounting platforms, and implementing web and mobile apps that allowed staff to manage their own schedules.`}
          heading="The Bird's Eye View"
          subHeading="summary"
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={laptopAndPhone}
              alt="A laptop and a phone displaying the SOL app"
              style={{ width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>

      <SpacingWrapper>
        <Section
          p1={`According to Statistics Canada, private nursing and residential care facilities hit revenues of $15.6 billion in 2020. With operating expenses pegged at $14.2 billion, salaries, wages, commissions and benefits comprised over half of the operating expenses at $7.8 billion.¹\nAmong public facilities, revenues reached $19 billion and operating expenses at $19.1 billion. The salaries, wages, commissions and benefits were at almost three-quarters of the expenses at $14.1 billion.²\n“My overtime and overhead costs are high.”\nThe relationship between Launchcode and Masterpiece Retirement began with that one statement. Okay, there were a few more steps before that, but you get the drift. `}
          p2={`A lot of scheduling and timesheeting processes at Masterpiece were done manually, and then typed in one entry at a time in Excel. As is the case in any organization, last-minute changes to staff schedules are inevitable, and also cumbersome to manage.\nMultiply these processes with the various levels of nursing staff and different departments, and it often snowballed into higher costs. And there was manual input of timesheets for payroll processing.\nOptimization was the need of the hour for Masterpiece, a balance that is efficient and boosts value. 
           `}
          heading="A Digital Health Solution That Does It All"
          subHeading="pain"
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={infographic}
              alt="An infographic displaying the revenue and operating expenses in nursing and residential care facilities in Canada by sector in 2020"
              style={{ width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>

      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <SectionTag>vision</SectionTag>
            <h2 style={{ fontWeight: 500, fontSize: 48, maxWidth: 730, textAlign: 'left' }}>
              <strong>A scheduling innovation in health care facilities </strong>
            </h2>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <p style={{ fontWeight: 400, fontSize: 20, lineHeight: '30px', color: '#616161' }}>
              A functioning retirement residence needs to strike a balance between providing
              round-the-clock hospitality and care, and having the right personnel at the right
              time.
              <br />
              <br />
              To ensure that, an ideal solution would have been a custom application that not only
              automates a lot of the manual data entry processes, but also allows employees to
              manage their schedules. And if the application can integrate well with the accounting
              and payroll processing software, that would be a cherry on top.
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>

      <SpacingWrapper>
        <Tech
          content={`The developers wanted to ensure simplicity for future maintenance and support, something that can be ensured by using a common framework for web and mobile applications. Enter JavaScript-React, which is one of Launchcode's many strengths.
              The mobile apps for Android and iOS would run on React Native. The web-based scheduling and approving tool would be created using React. Backend used NodeJS.
              The approach ensured minimal change in code for the multiple apps that would be needed.`}
          images={[reactLogo, javascriptLogo, nodeLogo]}
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={timesheet}
              alt="A laptop displaying SOL's timesheet page"
              style={{ width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>

      <SpacingWrapper>
        <Section
          p1={`Most big and efficient organizations do not want to change their processes to suit off-the-shelf solutions available, but want solutions that can work within the framework of the organization's workflows. 
          To this end, Launchcode's first step was to understand the business and processes at Masterpiece. With 100s of employees across multiple departments and hierarchies, the Launchcode team held interviews with key staff members from every department - nursing to housekeeping. 
          These interviews were instrumental in creating a custom algorithm that saw various iterations to ensure smooth and accurate workflow.`}
          p2={`With sprint methodology guiding the development process, the focus was to work on aspects that were of most value to the solution first. 
          Multiple two-week sprints went from implementing user authentication and profiles to the scheduling portal and its many user-friendly features. 
          The employee app, a key element that provided staff with the ability to control their schedules and plan their non-work lives ahead, was next. The app facilitated the use of message boards, shift-related requests and changes, push notifications to inform staff members of key events and updates, among other things. 
           `}
          heading="In the Quest for a Digital Health Solution"
          subHeading="journey"
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <Quote
          quote="Most organizations will have 30 odd problems. For a solution to work and have an impact, it is important that it is focused on the big tasks that comprise 80% of the problems. SOL focused on key issues - overtime costs, optimization and scheduling, and employee retention. That's why it is a success. "
          citation="Alberio Bathory-Frota"
          citation2="CEO @ Launchcode"
          picture={alberio}
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <MultipleDeviceImageWrapper>
              <img
                src={userExperiencePhone}
                alt="An iPhone displaying a shift swap on the SOL app"
              />
            </MultipleDeviceImageWrapper>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <SectionTag>ux/ui</SectionTag>
            <h2 style={{ fontWeight: 500, fontSize: 48, maxWidth: 730, textAlign: 'left' }}>
              <strong>What you see is what you get</strong>
            </h2>
            <p style={{ fontWeight: 400, fontSize: 20, lineHeight: '30px', color: '#616161' }}>
              Given the solution's use and implementation across departments, it was important for
              developers to ensure adoption and ease-of-use by staff members of diverse
              technological capabilities, across a wide range of screens and sizes.
              <br />
              <br />
              Visual cues were incorporated into the apps, while special focus was placed on
              ensuring that tasks required lesser number of user interactions and clicks for
              completion.
              <br />
              <br />
              Given the impact the application was intended to have on the organization and its
              people, training sessions were held for backend and frontend users.
              <br />
              <br />
              The team also created a quick guide and a detailed user guide.
              <br />
              <br />A quick guide, like the ones that are packaged with most new smartphones these
              days, would help majority of users through the most routine and basic tasks that they
              would encounter often. A detailed user guide would target users that were required to
              undertake more complex setup tasks.
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>

      <SpacingWrapper>
        <Section
          p1={`SOL emerged as an all-powerful scheduling and timesheeting platform for Masterpiece. It first saved the licensing fees company spent on a third-party timesheet tool and connected directly with the accounting platform. 
          Scheduling and timesheeting became almost 70% more efficient and now needed only one staff member to manage it all. 
          The AI algorithm-based SOL ensured optimum scheduling of nursing staff in particular and the entire organization in general, reducing unnecessary overtime and overhead costs. `}
          p2={`The mobile apps gave staff the flexibility and the convenience to manage their own schedules, giving them control over their work-life balance. 
          Masterpiece no longer relies on manual processes and Excel. With operating costs down, their valuation increased by over $8 million, and the client saw a return on investment (ROI) in less than 6 months. 
          Post-implementation analyses and surveys within the organization showed a reduction in sick time and increase in staff morale by 98%, with employee retention getting a boost. `}
          subHeading="results"
          heading="Making Scheduling A Breeze for Healthcare Businesses "
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <Quote
          quote="People know there is a better way, they just do not know how to do it. And that's where we come in. Our holistic approach, the understanding of client's business, the impact our solution will have on their business, that's key. "
          citation="Robbie Butchart"
          citation2="CRO @ Launchcode"
          picture={robbie}
        />
      </SpacingWrapper>

      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={background}
              alt="two iphones displaying the SOL app"
              style={{ width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>

      <SpacingWrapper>
        <Section
          p1={`Masterpiece was one of the first clients to throw a challenge at Launchcode for a custom solution in a very niche but huge market.`}
          p2={`Response at the initial mock-up stage itself was encouraging, with a request to “turn it on” immediately. The contagious jest and excitement aside, SOL's thoughtful and simple interface that included options to copy past schedules, the ability to add custom shifts, to tackle employee requests, etc. were well received. `}
          subHeading="response"
          heading="Clients Love Us!"
        />
      </SpacingWrapper>

      <Quote
        quote="Before Launchcode's solution, our entire staffing process was done manually. It was time-intensive and errors occurred frequently, which then added more time as management had to sort out inaccuracies. The app has greatly improved efficiency as well as staff morale, which were two main areas we were concerned with. Launchcode took extra steps to help us be successful, something that other companies wouldn't have."
        citation="Tim Garforth-Bles"
        citation2="President Masterpiece Care Corporation"
      />

      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <SectionTag>next steps</SectionTag>
            <h2 style={{ fontWeight: 500, fontSize: 48, maxWidth: 730, textAlign: 'left' }}>
              <strong>With this development company, innovation does not rest</strong>
            </h2>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <p style={{ fontWeight: 400, fontSize: 20, lineHeight: '30px', color: '#616161' }}>
              Launchcode is actively involved in the maintenance and support aspects of the
              solution. SOL gives the organization the flexibility to change accounting and other
              solutions that are integrated into the solution, among other things.
              <br />
              <br />
              With Phase 1 of SOL's implementation and mission a success, focus could soon turn to
              the second phase of the project depending on requirements and feasibility.
              <br />
              <br />
              Phase 2 involves setting up a customer relationship management (CRM) tool for
              residents that would centralize registration, services, food requirements, payments,
              the works.
              <br />
              <br />
              Along with the employee platform and app, Phase 2 will create a fantastic all-in-one
              powerful management tool that gives a holistic 360-degree solution a new meaning.
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>

      <SpacingWrapper>
        <CenteredContent>
          <SimpleUXImageWrapper>
            <img
              src={finalPhoto}
              alt="A woman sips coffee and looks at the SOL web app"
              style={{ width: '100%' }}
            />
          </SimpleUXImageWrapper>
        </CenteredContent>
      </SpacingWrapper>

      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <CitationsTitleWrapper>
              <h2
                style={{
                  fontWeight: 500,
                  fontSize: 48,
                  maxWidth: 730,
                  textAlign: 'left',
                }}
              >
                <strong>Extras</strong>
              </h2>
            </CitationsTitleWrapper>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <h3 style={{ fontWeight: 700, fontSize: 20 }}>Download the app from:</h3>
            <p style={{ fontWeight: 400, fontSize: 20, lineHeight: '30px', color: '#616161' }}>
              <a
                href="https://apps.apple.com/ca/app/sol-health-care-scheduling/id1477394016"
                rel="nofollow noreferrer"
                target="_blank"
              >
                App Store
              </a>{' '}
              and{' '}
              <a
                href="https://play.google.com/store/apps/details?id=com.servalldev.masterpiece.staging.employeeapp&hl=en_CA&gl=US"
                rel="nofollow noreferrer"
                target="_blank"
              >
                Play Store
              </a>
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>

      <SpacingWrapper>
        <ResponsiveDiv>
          <ResponsiveParagraph>
            <CitationsTitleWrapper>
              <h2
                style={{
                  fontWeight: 500,
                  fontSize: 48,
                  maxWidth: 730,
                  textAlign: 'left',
                }}
              >
                <strong>Citations</strong>
              </h2>
            </CitationsTitleWrapper>
          </ResponsiveParagraph>
          <ResponsiveParagraph>
            <p>
              <sup>1 & 2</sup>
              <a
                href="https://www150.statcan.gc.ca/n1/daily-quotidien/220303/dq220303a-eng.htm"
                target="_blank"
                rel="nofollow noreferrer"
              >
                “Nursing and residential care facilities in 2020: Implications from the first year
                of the pandemic”.
              </a>{' '}
              Statistics Canada, 3 March 2022. Accessed 9 May 2022.
            </p>
          </ResponsiveParagraph>
        </ResponsiveDiv>
      </SpacingWrapper>

      <NextMissions currentProjectName="SOL" projects={projects} />

      <CenteredContent style={{ marginBottom: 40 }}>
        <a href="#top">Back to top ⇮</a>
      </CenteredContent>
    </>
  );
};

const MultipleDeviceImageWrapper = styled.div`
  width: 100%;
  height: 100%;

  > div {
    transform: scale(1.075);
  }
`;

const SimpleUXImageWrapper = styled.div`
  > div {
    overflow: unset;
    transform: scale(1.075);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    img {
      top: 12.5%;
    }
  }
`;

const CitationsTitleWrapper = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.xs}) {
    margin: 0px 0px 0px 0px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin: 0px 0px 0px 0px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-left: 40px;
  }

  @media screen and (min-width: 1250px) {
    margin-left: 40px;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.wideScreen}) {
    margin-left: 40px;
  }
`;

export default SolPage;
